import { z } from 'zod';
/**
 * Remise sur le prochain renouvellement d'abonnement — `docs/specs-v2/07-remise-renouvellement.md`.
 */
/** Sorte d'opération commerciale (`07` §1). Les opérations antérieures sont `free_product` (migration 012). */
export const OPERATION_KINDS = ['free_product', 'renewal_discount'];
export const operationKindSchema = z.enum(OPERATION_KINDS);
export const OPERATION_KIND = {
    free_product: {
        code: 'free_product',
        label: 'Produit offert',
        tone: 'blue',
        description: 'Ajoute un ou plusieurs produits aux commandes envoyées à Martelet.',
    },
    renewal_discount: {
        code: 'renewal_discount',
        label: 'Remise renouvellement',
        tone: 'green',
        description: 'Pose une remise sur le prochain renouvellement d’abonnement Recharge de chaque client.',
    },
};
/** Calcul de la remise (`07` §3). */
export const RENEWAL_DISCOUNT_CALCULATIONS = ['additional', 'target'];
export const renewalDiscountCalculationSchema = z.enum(RENEWAL_DISCOUNT_CALCULATIONS);
export const RENEWAL_DISCOUNT_CALCULATION = {
    additional: {
        code: 'additional',
        label: 'En plus des remises actuelles',
        tone: 'gray',
        description: 'Le taux s’applique au prix actuel du renouvellement, remise abonnement et remise du bundle déjà déduites.',
    },
    target: {
        code: 'target',
        label: 'Remise totale visée',
        tone: 'gray',
        description: 'Le taux est la remise totale par rapport au prix Shopify des produits. Flux ne pose que l’écart avec les remises déjà intégrées au prix.',
    },
};
/** Règle quand le renouvellement porte déjà un code de réduction (`07` §2.3). */
export const EXISTING_DISCOUNT_RULES = ['ignore', 'replace', 'defer'];
export const existingDiscountRuleSchema = z.enum(EXISTING_DISCOUNT_RULES);
export const EXISTING_DISCOUNT_RULE = {
    ignore: {
        code: 'ignore',
        label: 'Ignorer',
        tone: 'gray',
        description: 'Le client garde son code et ne reçoit pas la remise de l’opération.',
    },
    replace: {
        code: 'replace',
        label: 'Remplacer',
        tone: 'orange',
        description: 'Flux retire le code du client et pose la remise de l’opération. L’ancien code est conservé dans l’historique.',
    },
    defer: {
        code: 'defer',
        label: 'Décaler',
        tone: 'blue',
        description: 'Le client garde son code sur ce renouvellement ; la remise de l’opération ira sur son renouvellement suivant sans code, s’il tombe dans la période.',
    },
};
/** Cycle de vie d'une opération « remise renouvellement » (`07` §4). */
export const RENEWAL_DISCOUNT_STATES = ['draft', 'active', 'ended', 'removed'];
export const renewalDiscountStateSchema = z.enum(RENEWAL_DISCOUNT_STATES);
export const RENEWAL_DISCOUNT_STATE = {
    draft: { code: 'draft', label: 'Brouillon', tone: 'gray', description: 'Pas encore activée : aucune remise posée.' },
    active: { code: 'active', label: 'Activée', tone: 'green' },
    ended: {
        code: 'ended',
        label: 'Désactivée',
        tone: 'gray',
        description: 'Plus aucune nouvelle remise. Les remises déjà posées restent sur les renouvellements.',
    },
    removed: { code: 'removed', label: 'Remises retirées', tone: 'red', description: 'Toutes les remises en attente ont été retirées.' },
};
/** Statut d'un client dans une opération (`07` §5, collection `rechargeDiscountApplications`). */
export const RENEWAL_DISCOUNT_APPLICATION_STATUSES = [
    'applied',
    'replaced',
    'existing_discount',
    'deferred',
    'at_target',
    'no_tier',
    'legacy',
    'excluded',
    'lost',
    'out_of_window',
    'consumed',
    'removed',
    'error',
];
export const renewalDiscountApplicationStatusSchema = z.enum(RENEWAL_DISCOUNT_APPLICATION_STATUSES);
export const RENEWAL_DISCOUNT_APPLICATION_STATUS = {
    applied: { code: 'applied', label: 'Remise posée', tone: 'green' },
    replaced: { code: 'replaced', label: 'Code remplacé', tone: 'green', description: 'Le code du client a été retiré au profit de la remise.' },
    existing_discount: { code: 'existing_discount', label: 'Code existant', tone: 'gray', description: 'Le renouvellement porte déjà un code : rien n’a été changé.' },
    deferred: { code: 'deferred', label: 'Décalée', tone: 'blue', description: 'En attente d’un renouvellement sans code dans la période.' },
    at_target: { code: 'at_target', label: 'Déjà au tarif', tone: 'gray', description: 'Le prix actuel est déjà au niveau de la remise visée.' },
    no_tier: { code: 'no_tier', label: 'Hors palier', tone: 'gray', description: 'Pas assez de produits pour le premier palier.' },
    legacy: {
        code: 'legacy',
        label: 'Abonnement legacy',
        tone: 'gray',
        description: 'Le renouvellement ne contient que des abonnements legacy, exclus par l’opération.',
    },
    excluded: { code: 'excluded', label: 'Nouvel abonné', tone: 'gray', description: 'Abonné après l’activation : exclu de l’opération.' },
    lost: { code: 'lost', label: 'Remise perdue', tone: 'red', description: 'Le code n’est plus sur le renouvellement (modifié par le client ou le support) : Flux ne le repose pas.' },
    out_of_window: { code: 'out_of_window', label: 'Hors période', tone: 'gray', description: 'Le renouvellement est sorti de la période.' },
    consumed: { code: 'consumed', label: 'Consommée', tone: 'green', description: 'Renouvellement payé avec la remise.' },
    removed: { code: 'removed', label: 'Retirée', tone: 'gray' },
    error: { code: 'error', label: 'Erreur', tone: 'red' },
};
/**
 * Actions d'historique d'un client qui ne sont pas des statuts (`07` §5.4) :
 * l'historique enregistre le plus souvent le statut atteint, parfois une étape
 * intermédiaire (retrait du code du client avant la pose, règle « Remplacer »).
 */
export const RENEWAL_DISCOUNT_HISTORY_STEPS = ['replacing'];
export const RENEWAL_DISCOUNT_HISTORY_STEP = {
    replacing: {
        code: 'replacing',
        label: 'Code client retiré pour remplacement',
        tone: 'orange',
        description: 'Le code du client a été retiré pour poser la remise de l’opération.',
    },
};
/** Repli lisible pour une action d'historique inconnue de cette version (jamais le code brut). */
const UNKNOWN_HISTORY_ACTION_LABEL = 'Autre événement';
/**
 * Libellé et teinte d'une action d'historique : statut client, étape connue,
 * ou repli générique.
 */
export function renewalDiscountHistoryAction(action) {
    const status = renewalDiscountApplicationStatusSchema.safeParse(action);
    if (status.success) {
        return RENEWAL_DISCOUNT_APPLICATION_STATUS[status.data];
    }
    const step = z.enum(RENEWAL_DISCOUNT_HISTORY_STEPS).safeParse(action);
    if (step.success) {
        return RENEWAL_DISCOUNT_HISTORY_STEP[step.data];
    }
    return { code: action, label: UNKNOWN_HISTORY_ACTION_LABEL, tone: 'gray' };
}
/** Origine du dernier traitement d'un client. */
export const RENEWAL_DISCOUNT_SOURCES = ['initial', 'webhook', 'reconcile'];
export const renewalDiscountSourceSchema = z.enum(RENEWAL_DISCOUNT_SOURCES);
export const RENEWAL_DISCOUNT_SOURCE = {
    initial: { code: 'initial', label: 'Passe initiale', tone: 'blue' },
    webhook: { code: 'webhook', label: 'Webhook', tone: 'gray' },
    reconcile: { code: 'reconcile', label: 'Réconciliation', tone: 'gray' },
};
/** Exécutions longues d'une opération (`rechargeRuns`). */
export const RENEWAL_DISCOUNT_RUN_KINDS = ['preview', 'initial', 'reconcile', 'remove'];
export const renewalDiscountRunKindSchema = z.enum(RENEWAL_DISCOUNT_RUN_KINDS);
export const RENEWAL_DISCOUNT_RUN_KIND = {
    preview: { code: 'preview', label: 'Aperçu', tone: 'gray' },
    initial: { code: 'initial', label: 'Passe initiale', tone: 'blue' },
    reconcile: { code: 'reconcile', label: 'Réconciliation', tone: 'gray' },
    remove: { code: 'remove', label: 'Retrait des remises', tone: 'red' },
};
/**
 * Statuts de l'aperçu (`07` §6.2) : rien n'est encore posé, la remise est
 * « prévue ». Mêmes codes et couleurs que `RENEWAL_DISCOUNT_APPLICATION_STATUS`.
 */
export const RENEWAL_DISCOUNT_PREVIEW_STATUS = {
    ...RENEWAL_DISCOUNT_APPLICATION_STATUS,
    applied: { code: 'applied', label: 'Remise prévue', tone: 'green' },
    replaced: { code: 'replaced', label: 'Code à remplacer', tone: 'green', description: 'Le code du client sera retiré au profit de la remise.' },
};
/** Modes de la fonction « Remise renouvellement » : pas de simulation (`07` §7). */
export const RENEWAL_DISCOUNT_FEATURE_MODES = ['disabled', 'active'];
export const renewalDiscountFeatureModeSchema = z.enum(RENEWAL_DISCOUNT_FEATURE_MODES);
//# sourceMappingURL=renewal-discount.js.map